// src/components/conversation/ConversationPanel.js
import React,{ useCallback,useEffect,useRef,useState } from 'react';
import { FaExclamationTriangle, FaPaperPlane } from 'react-icons/fa';
import { toast } from 'react-toastify';
import useWebSocket from '../../hooks/useWebSocket';
import LoadingSpinner from '../common/LoadingSpinner';
import './ConversationPanel.css';
import { useAuth } from '../../context/AuthContext';

const ConversationPanel = () => {
  const [conversations, setConversations] = useState([]);
  const [customCommand, setCustomCommand] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [presetCommands, setPresetCommands] = useState([]);
  const messagesEndRef = useRef(null);
  const messageIdCounterRef = useRef(0); // To track message uniqueness
  const API_BASE_URL = '';
  const API_PATH = '/api';
  const API_WS_URL = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
  const { apiRequest } = useAuth();
  // WebSocket message handler
  const handleWebSocketMessage = useCallback((message) => {
    try {
      
      
      // Only add messages from the assistant or system through WebSocket
      if (message.type === 'assistant' || (message.type === 'system' && !message.fromLocal)) {
        setConversations(prev => [
          ...prev,
          { 
            ...message, 
            id: `ws-${messageIdCounterRef.current++}`, 
            timestamp: new Date() 
          }
        ]);
      }
      
      if (message.type === 'assistant') setProcessing(false);
    } catch (error) {
      console.error('Error processing conversation message:', error);
    }
  }, []);

  const getToken = () => localStorage.getItem('authToken'); // or from Redux/context
  // Initialize WebSocket
  const { send: sendWebSocket } = useWebSocket(
    `${API_WS_URL}/ws/conversation`,
    handleWebSocketMessage,getToken
  );

  const initializedRef = useRef(false);

useEffect(() => {
  const initConversation = async () => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    try {
      setLoading(true);
      const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/conversation/commands`);
      if (!response.ok) throw new Error('Failed to load conversation commands');

      setPresetCommands(data.commands || []);
      setConversations(prev => [
        ...prev,
        {
          id: `system-${messageIdCounterRef.current++}`,
          type: 'system',
          message: 'Welcome to your Smart Home Assistant',
          timestamp: new Date(),
          fromLocal: true
        }
      ]);
      setError(null);
    } catch (error) {
      console.error('Error initializing conversation:', error);
      setError('Failed to load conversation interface. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  initConversation();
}, [apiRequest]);

  // Scroll to bottom when conversations change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversations]);

  // Handle custom command submission
  const handleCommandSubmit = async (e) => {
    e.preventDefault();
    if (!customCommand.trim() || processing) return;
    
    const userMessage = {
      id: `user-${messageIdCounterRef.current++}`,
      type: 'user',
      message: customCommand,
      timestamp: new Date(),
      fromLocal: true
    };
    
    setConversations(prev => [...prev, userMessage]);
    setProcessing(true);
    
    try {
      await apiRequest(`${API_BASE_URL}${API_PATH}/conversation/custom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          command: customCommand,
          messageId: userMessage.id // Send ID to potentially track on server
        })
      });
    } catch (error) {
      console.error('Error sending custom command:', error);
      toast.error('Failed to send command');
      setProcessing(false);
      setConversations(prev => [
        ...prev,
        { 
          id: `error-${messageIdCounterRef.current++}`,
          type: 'system', 
          message: 'Failed to process your command. Please try again.',
          timestamp: new Date(),
          isError: true,
          fromLocal: true
        }
      ]);
    }
    
    setCustomCommand('');
  };

  // Execute preset command
  const executePresetCommand = async (command) => {
    if (processing) return;
    
    const userMessage = {
      id: `preset-${messageIdCounterRef.current++}`,
      type: 'user',
      message: command,
      timestamp: new Date(),
      fromLocal: true
    };
    
    setConversations(prev => [...prev, userMessage]);
    setProcessing(true);
    
    try {
      await apiRequest(`${API_BASE_URL}${API_PATH}/conversation/preset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          command,
          messageId: userMessage.id // Send ID to potentially track on server
        })
      });
    } catch (error) {
      console.error('Error sending preset command:', error);
      toast.error('Failed to send command');
      setProcessing(false);
      setConversations(prev => [
        ...prev,
        { 
          id: `error-${messageIdCounterRef.current++}`,
          type: 'system', 
          message: 'Failed to process your command. Please try again.',
          timestamp: new Date(),
          isError: true,
          fromLocal: true
        }
      ]);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Default preset commands if none returned from API
  const defaultPresets = presetCommands.length > 0 ? presetCommands : [
    'Turn off all lights',
    'Close all curtains',
    'What\'s the temperature?',
    'Set thermostat to 22 degrees',
    'Turn on TV in living room',
    'Activate Welcome scene'
  ];

  if (loading) {
    return (
      <div className="conversation-loading">
        <LoadingSpinner size="large" text="Initializing conversation..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversation-error">
        <div className="error-icon">
          <FaExclamationTriangle />
        </div>
        <h3>Connection Error</h3>
        <p>{error}</p>
        <button 
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="conversation-container">
      <div className="conversation-header">
        <h2>Smart Home Assistant</h2>
        <p>Use voice commands or type to control your smart home</p>
      </div>
      
      <div className="conversation-main">
        <div className="preset-commands">
          <h3>Quick Commands</h3>
          <div className="commands-buttons">
            {defaultPresets.map((command, index) => (
              <button 
                key={index}
                className="preset-command-button"
                onClick={() => executePresetCommand(command)}
                disabled={processing}
              >
                {command}
              </button>
            ))}
          </div>
        </div>
        
        <div className="conversation-messages">
          <div className="messages-container">
            {conversations.map((msg) => (
              <div 
                key={msg.id} 
                className={`message ${msg.type} ${msg.isError ? 'error' : ''}`}
              >
                <div className="message-content">
                  {msg.message}
                </div>
                <div className="message-timestamp">
                  {formatTimestamp(msg.timestamp)}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
            
            {processing && (
              <div className="message assistant processing">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <form className="message-input" onSubmit={handleCommandSubmit}>
            <input 
              type="text"
              placeholder="Type your command here..."
              value={customCommand}
              onChange={(e) => setCustomCommand(e.target.value)}
              disabled={processing}
            />
            <button 
              type="submit"
              disabled={processing || !customCommand.trim()}
            >
              <FaPaperPlane />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConversationPanel;